import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Tooltip, Paper, TextField, Button, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

/**
 * TextSelectionPopup — Appears when user selects text inside a content container.
 * Shows a small "Add Comment" button at the selection position.
 * On click, expands into an inline comment form with the quoted text.
 *
 * Props:
 *   containerRef   — ref to the DOM element to listen for text selection
 *   sectionId      — which section the selection is in
 *   onSubmitComment({ sectionId, content, selectedText, textAnchor }) — callback
 *   disabled       — disable selection (e.g., during edit mode)
 */
const TextSelectionPopup = ({ containerRef, sectionId, onSubmitComment, disabled = false }) => {
  const [selection, setSelection] = useState(null); // { text, rect, anchor }
  const [showForm, setShowForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  // Capture text selection on mouseup
  const handleMouseUp = useCallback(() => {
    if (disabled) return;

    // Small delay to let browser finalize selection
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.toString().trim()) {
        // User clicked without selecting text — dismiss existing popup
        // but only if we're not in the expanded form (don't dismiss mid-comment)
        if (!showForm) dismiss();
        return;
      }

      const selectedText = sel.toString().trim();
      if (selectedText.length < 3) return; // Ignore tiny selections

      // Check selection is within our container
      const container = containerRef?.current;
      if (!container) return;
      const range = sel.getRangeAt(0);
      // Check: both start and end must be inside this container
      // (prevents capturing selections that span multiple sections)
      const startInside = container.contains(range.startContainer);
      const endInside = container.contains(range.endContainer);
      if (!startInside || !endInside) return;

      // Get position for the popup
      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Build text anchor for fuzzy re-anchoring
      const fullText = container.textContent || '';
      const startIdx = fullText.indexOf(selectedText);
      const contextBefore = startIdx > 0 ? fullText.substring(Math.max(0, startIdx - 50), startIdx) : '';
      const contextAfter = fullText.substring(startIdx + selectedText.length, startIdx + selectedText.length + 50);

      setSelection({
        text: selectedText,
        rect: {
          top: rect.top - containerRect.top,
          left: rect.left - containerRect.left + rect.width / 2,
        },
        anchor: {
          start_offset: startIdx,
          end_offset: startIdx + selectedText.length,
          context_before: contextBefore,
          context_after: contextAfter,
        }
      });
    }, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, disabled, showForm]);

  // Listen for mouseup on the container
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, [containerRef, handleMouseUp]);

  // Dismiss on click outside or Escape — but NOT when clicking the popup itself
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't dismiss if clicking inside the popup
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      // Don't dismiss if clicking inside the container (user might be starting new selection)
      const container = containerRef?.current;
      if (container && container.contains(e.target)) return;
      // Clicking outside both → dismiss
      dismiss();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') dismiss();
    };

    if (selection) {
      // Use click (not mousedown) to avoid dismissing during text selection drag
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selection]);

  // Focus input when form opens
  useEffect(() => {
    if (showForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showForm]);

  const dismiss = () => {
    setSelection(null);
    setShowForm(false);
    setCommentText('');
  };

  const handleSubmit = async () => {
    if (!commentText.trim() || !selection) return;
    await onSubmitComment({
      sectionId,
      content: commentText.trim(),
      selectedText: selection.text,
      textAnchor: selection.anchor,
    });
    dismiss();
  };

  if (!selection) return null;

  // Position popup BELOW selection by default, or above if near bottom
  // Clamp to stay visible within container
  const popupTop = Math.max(0, selection.rect.top - (showForm ? 10 : 30));
  const popupLeft = Math.max(10, Math.min(selection.rect.left - (showForm ? 150 : 40), 500));

  return (
    <Box
      ref={popupRef}
      sx={{
        position: 'absolute',
        top: popupTop,
        left: popupLeft,
        zIndex: 100,
      }}
    >
      {!showForm ? (
        /* Small floating button */
        <Paper
          elevation={4}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.5, borderRadius: 2,
            bgcolor: '#475969', cursor: 'pointer',
          }}
          onClick={() => setShowForm(true)}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: '#fff' }} />
          <Typography sx={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>
            Comment
          </Typography>
        </Paper>
      ) : (
        /* Expanded comment form */
        <Paper
          elevation={6}
          sx={{
            width: 320, p: 2, borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          {/* Quoted text */}
          <Box sx={{
            p: 1, mb: 1.5, bgcolor: '#f8f9fa', borderRadius: 1,
            borderLeft: '3px solid #1976d2', maxHeight: 60, overflow: 'hidden',
          }}>
            <Typography sx={{ fontSize: 11, color: '#4a5568', fontStyle: 'italic', lineHeight: 1.3 }}>
              "{selection.text.length > 100 ? selection.text.substring(0, 100) + '...' : selection.text}"
            </Typography>
          </Box>

          <TextField
            inputRef={inputRef}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add your comment..."
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: 13,
                '& fieldset': { borderColor: '#e0e0e0' },
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
            <Button
              size="small"
              onClick={dismiss}
              sx={{ textTransform: 'none', fontSize: 11, color: '#72849a' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!commentText.trim()}
              onClick={handleSubmit}
              endIcon={<SendIcon sx={{ fontSize: 12 }} />}
              sx={{
                textTransform: 'none', fontSize: 11, fontWeight: 500,
                bgcolor: '#475969', '&:hover': { bgcolor: '#34454f' },
                px: 1.5, py: 0.25, minHeight: 28, borderRadius: 1,
              }}
            >
              Comment
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default React.memo(TextSelectionPopup);
